import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import { getComicsByCategory } from "@/mocks/comics";
import { categories, categoryMap } from "@/mocks/categories";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || "all";
  const [sortBy, setSortBy] = useState<"latest" | "popular" | "rating">("latest");

  const categoryName = categoryMap[activeSlug] || "全部";

  const filteredComics = useMemo(() => {
    const comics = getComicsByCategory(activeSlug);
    switch (sortBy) {
      case "popular":
        return [...comics].sort((a, b) => b.views - a.views);
      case "rating":
        return [...comics].sort((a, b) => b.rating - a.rating);
      case "latest":
      default:
        return [...comics].sort(
          (a, b) =>
            new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
        );
    }
  }, [activeSlug, sortBy]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-24 pb-16 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "漫画分类", to: "/category/all" },
              { label: categoryName },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
              {categoryName === "全部" ? "全部韩漫" : `${categoryName}韩漫`}
            </h1>
            <p className="text-foreground-500 text-sm mt-2">
              {categoryName === "全部"
                ? "浏览所有类型的韩国漫画，发现您喜爱的作品"
                : `浏览${categoryName}类型的韩国漫画，韩漫在线观看免费下拉式阅读`}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  if (cat.slug !== activeSlug) {
                    window.REACT_APP_NAVIGATE(`/category/${cat.slug}`);
                  }
                }}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-200/60 text-foreground-400 hover:text-foreground-200 hover:bg-background-300/50"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs text-foreground-500 whitespace-nowrap">
              共 {filteredComics.length} 部作品
            </span>
            <div className="flex-1" />
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { key: "latest", label: "最新" },
                { key: "popular", label: "热门" },
                { key: "rating", label: "评分" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key as typeof sortBy)}
                  className={`px-4 py-1.5 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "text-foreground-500 hover:text-foreground-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {filteredComics.map((comic) => (
              <div key={comic.id} className="flex justify-center">
                <ComicCard comic={comic} size="medium" />
              </div>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
                <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画作品</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}